export const REVIEW_OUTCOME = {
  PENDING: "pending",
  DELETED: "deleted",
  NOT_DELETED: "not_deleted",
};

const REVIEW_OUTCOME_VALUES = new Set(Object.values(REVIEW_OUTCOME));

/** Coerce API/draft values to a known review outcome (defaults to pending). */
export function normalizeReviewOutcome(value) {
  if (value && REVIEW_OUTCOME_VALUES.has(value)) return value;
  return REVIEW_OUTCOME.PENDING;
}

/** Read a draft entry using the same id coercion rules as draft writes. */
export function getDraftReviewOutcome(draft, itemId, fallback = REVIEW_OUTCOME.PENDING) {
  if (itemId == null) return normalizeReviewOutcome(fallback);
  const raw = draft[itemId] ?? draft[String(itemId)];
  return normalizeReviewOutcome(raw ?? fallback);
}

/** Current outcomes affected by each bulk "mark all" action. */
export const MARK_ALL_SOURCE_OUTCOMES = {
  [REVIEW_OUTCOME.DELETED]: [REVIEW_OUTCOME.PENDING],
  [REVIEW_OUTCOME.NOT_DELETED]: [REVIEW_OUTCOME.PENDING],
  [REVIEW_OUTCOME.PENDING]: [REVIEW_OUTCOME.DELETED, REVIEW_OUTCOME.NOT_DELETED],
};

export function applyBulkReviewOutcome(draft, items, targetOutcome) {
  const sourceOutcomes = MARK_ALL_SOURCE_OUTCOMES[targetOutcome];
  if (!sourceOutcomes?.length) return draft;

  const next = { ...draft };
  items.forEach((item) => {
    const current = getDraftReviewOutcome(draft, item.id);
    if (sourceOutcomes.includes(current)) {
      next[item.id] = targetOutcome;
    }
  });
  return next;
}

export function countBulkReviewOutcomeTargets(draft, items, targetOutcome) {
  const sourceOutcomes = MARK_ALL_SOURCE_OUTCOMES[targetOutcome];
  if (!sourceOutcomes?.length) return 0;

  return items.filter((item) => {
    const current = getDraftReviewOutcome(draft, item.id);
    return sourceOutcomes.includes(current);
  }).length;
}

export const REVIEW_OUTCOME_LABELS = {
  pending: "Pending",
  deleted: "Removed",
  not_deleted: "Removal failed",
};

export const REVIEW_OUTCOME_OPTIONS = [
  { value: REVIEW_OUTCOME.PENDING, label: REVIEW_OUTCOME_LABELS.pending },
  { value: REVIEW_OUTCOME.DELETED, label: REVIEW_OUTCOME_LABELS.deleted },
  {
    value: REVIEW_OUTCOME.NOT_DELETED,
    label: REVIEW_OUTCOME_LABELS.not_deleted,
  },
];

export function normalizeOutcomeSummary(raw) {
  if (!raw) {
    return {
      total: 0,
      deleted: 0,
      notDeleted: 0,
      pending: 0,
      resolved: 0,
      allResolved: false,
      isPartial: false,
    };
  }
  return {
    total: raw.total ?? 0,
    deleted: raw.deleted ?? 0,
    notDeleted: raw.not_deleted ?? raw.notDeleted ?? 0,
    pending: raw.pending ?? 0,
    resolved: raw.resolved ?? 0,
    allResolved: Boolean(raw.all_resolved ?? raw.allResolved),
    isPartial: Boolean(raw.is_partial ?? raw.isPartial),
  };
}

/** Build outcome summary from normalized review items (client/admin optimistic UI). */
export function computeOutcomeSummaryFromItems(items) {
  const list = Array.isArray(items) ? items : [];
  let deleted = 0;
  let notDeleted = 0;
  let pending = 0;

  list.forEach((item) => {
    const outcome = normalizeReviewOutcome(item.deletionOutcome);
    if (outcome === REVIEW_OUTCOME.DELETED) deleted += 1;
    else if (outcome === REVIEW_OUTCOME.NOT_DELETED) notDeleted += 1;
    else pending += 1;
  });

  const total = list.length;
  const resolved = deleted + notDeleted;

  return {
    total,
    deleted,
    notDeleted,
    pending,
    resolved,
    allResolved: total > 0 && pending === 0,
    isPartial: false,
  };
}

/** Apply saved per-review outcomes onto a case payload for immediate UI sync. */
export function mergeSavedItemOutcomes(caseData, itemOutcomes) {
  if (!caseData || !itemOutcomes?.length) return caseData;

  const byId = new Map(
    itemOutcomes.map((row) => [
      Number(row.id),
      normalizeReviewOutcome(row.deletion_outcome ?? row.deletionOutcome),
    ])
  );

  const items = (caseData.items || []).map((item) =>
    byId.has(item.id) ? { ...item, deletionOutcome: byId.get(item.id) } : item
  );

  return {
    ...caseData,
    items,
    outcomeSummary: computeOutcomeSummaryFromItems(items),
  };
}

export function outcomeBadgeClass(outcome) {
  switch (outcome) {
    case REVIEW_OUTCOME.DELETED:
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case REVIEW_OUTCOME.NOT_DELETED:
      return "bg-red-50 text-red-800 ring-red-200";
    default:
      return "bg-slate-50 text-slate-600 ring-slate-200";
  }
}

export function formatOutcomeSummaryShort(summary) {
  if (!summary || summary.total === 0) return "—";
  const parts = [];
  if (summary.deleted > 0) parts.push(`${summary.deleted} removed`);
  if (summary.notDeleted > 0) parts.push(`${summary.notDeleted} removal failed`);
  if (summary.pending > 0) parts.push(`${summary.pending} pending`);
  return parts.join(" · ") || "—";
}
